import { Map, Record } from 'immutable';
import { assign, compact, isArray, isString, trim } from 'lodash';
import React from 'react';
import { getCatDataByID } from './immutableUtils';
import appConfig from '../appConfig';

const SearchTermRecordData = new Map({ keyword: 'DEFAULT', value: undefined, operator: 'AND' });

export const getTypeLabel = (type) => {
  let msg = 'searchBar.simple.typeAny';
  switch (type) {
    case 'PHOTO':
      msg = 'searchBar.simple.typeImage';
      break;
    case 'VIDEO':
      msg = 'searchBar.simple.typeVideo';
      break;
    case 'AUDIO':
      msg = 'searchBar.simple.typeAudio';
      break;
    case 'DOCUMENT':
      msg = 'searchBar.simple.typeDocument';
      break;
    case 'RELATIONAL':
      msg = 'searchBar.simple.typeRecord';
      break;
    case undefined:
    case null:
    default:
      break;
  }
  return msg;
}

export const AllowedTypeFilters = ['PHOTO', 'VIDEO', 'AUDIO', 'DOCUMENT', 'RELATIONAL'];
export const RangeTypeOptions = [{ label: 'LESS', value: '<' }, { label: 'GREATER', value: '>' }, { label: 'RANGE', value: 'BETWEEN' }];

// this should probably extend SearchTerm somehow
export class MetadataSearchTerm extends new Record(SearchTermRecordData.merge(new Map({
  name: undefined,
  category: undefined,
  fullName: undefined,
  keyword: 'META',
})).toJS()) {
  constructor(category, name, value, options = {}) {
    super({
      category,
      name,
      value,
      fullName: category && name ? `${category}.${name}` : undefined,
      ...options,
    });
  }
  static get errorMessages() {
    return {
    };
  }
  toSearchQuery() {
    let value = '';
    if (this.value) value = this.value.replace(/"/g, '\\"');
    return this.keyword && this.fullName && value ?
      `${this.keyword}:"${this.fullName},${value}"` : '';
  }
  toSearchQueryWithOperator() {
    const asSearchQuery = this.toSearchQuery();
    return asSearchQuery !== '' ? `${this.operator} ${asSearchQuery}` : '';
  }
}

export class AdvancedSearchTerm extends new Record(SearchTermRecordData.merge(new Map({
  name: undefined,
  category: undefined,
  fullName: undefined,
  keyword: 'META',
  type: undefined,
})).toJS()) {
  constructor(category, name, value, options = {}) {
    super({
      category,
      name,
      value,
      fullName: category && name ? `${category}.${name}` : undefined,
      ...options,
    });
  }
  static get errorMessages() {
    return {
    };
  }
  /** META_RANGE:"fullName,type,range,val1[,val2]"
    where
    fullName - tag name or tag PK e.g. MYTAG.NUMBER
    type - "DATE" | "NUMBER" , so is this a date check or number check
    range = < | <= | > | >= | = | != | "BETWEEN" | "NOT BETWEEN"
    currently supporting only > | < | BETWEEN (LESS,GREATER,RANGE)
    val1 = date | number
    val2 = date | number (if BETWEEN or NOT BETWEEN)

    Example usage:
    meta_range:"GENERAL.CATEGORY,DATE,>,10-OCT-2019"
    meta_range:"GENERAL.CATEGORY,DATE,NOT BETWEEN,10-OCT-2019,20-OCT-2019"
    meta_range:"TECHNICAL.APERTUREVALUE,NUMBER,=,2"
    meta_range:"TECHNICAL.APERTUREVALUE,NUMBER,BETWEEN,2,3"
*/
  toRangeQuery(rangeIndex) {
    const key = this.operator && this.operator === 'NOT' && this.keyword ? 'META_RANGE_NOT' : 'META_RANGE';
    let value = this.value.toUpperCase();
    value = trim(value, `${RangeTypeOptions[rangeIndex].label}:`);
    value = value.replace(/['"]/g, '');
    return this.fullName && value ?
      `${key}:"${this.fullName},${this.type},${RangeTypeOptions[rangeIndex].value},${value}"` : '';
  }
  toSearchQuery() {
    let value = '';
    // Based on search term change asked by Marcelle
    const key = this.operator && this.operator === 'NOT' && this.keyword ? 'META_NOT' : 'META';
    const isRangeSearch =
      RangeTypeOptions.findIndex(e => this.value.toUpperCase().includes(e.label));
    if (this.value && isRangeSearch !== -1 && ['NUMBER', 'DATE'].includes(this.type)) {
      return this.toRangeQuery(isRangeSearch);
    }
    if (this.value) value = this.value.replace(/"/g, '\\"');
    return this.fullName && value ?
      `${key}:"${this.fullName},${value}"` : '';
  }
  toSearchQueryWithOperator() {
    const searchQuery = this.toSearchQuery();
    let op = '';
      // Based on search term change asked by Marcelle
    if (this.operator) op = this.operator === 'NOT' ? 'AND' : this.operator;

    return op && searchQuery ?
      `${op} ${searchQuery}` : '';
  }
}


export class SearchTerm extends new Record(SearchTermRecordData.toJS()) {
  constructor(keyword, value, options = {}) {
    // if keyword is null then assume that value is a fully formed query
    // i.e. it will not be modified before submitting the search
    if (!keyword) {
      super(assign({ value, keyword: null, ...options }));
    } else {
      super(assign({ value, ...options }, keyword && { keyword }));
    }
  }
  static get errorMessages() {
    return {
    };
  }
  toSearchQuery() {
    if (this.keyword) {
      return !this.value ? '' : `${this.keyword}:"${this.value}"`;
    }
    return !this.value ? '' : `${this.value}`;
  }
  toSearchQueryWithOperator() {
    if (this.operator && this.operator === 'NOT' &&
      this.keyword && this.keyword === 'DEFAULT') {
      return !this.value ? '' : `AND DEFAULT_NOT:"${this.value}"`;
    }
    return !this.value ? '' : `${this.operator} ${this.toSearchQuery()}`;
  }
}

/**
 * Used for storing/parsing the search parameters of the search.
 * i.e. the data that will go into the SEARCH parameter of the image query call
 */
export class SearchParams extends new Record({
  filterByList: undefined,
  typeFilter: undefined,
  searchQuery: undefined,
  metadataList: undefined, // i.e. the metadata list for the advanced search
  tagList: undefined,
  savedSearch: undefined,
  collectionID: undefined,
  displayTags: true,
}) {
  constructor(params = {}) {
    if (params.filterByList) {
      if (!isArray(params.filterByList)) {
        throw new Error(SearchParams.errorMessages.INVALID_FILTERBYLIST);
      }

      for (let i = 0; i < params.filterByList.length; i += 1) {
        if (!(params.filterByList[i] instanceof MetadataSearchTerm) &&
          !(params.metadataList[i] instanceof AdvancedSearchTerm) &&
          !(params.filterByList[i] instanceof SearchTerm)) {
          throw new Error(SearchParams.errorMessages.INVALID_FILTERBYLIST);
        }
      }
    } else if (params.typeFilter && (!isString(params.typeFilter) ||
      AllowedTypeFilters.indexOf(params.typeFilter) === -1)) {
      throw new Error(SearchParams.errorMessages.INVALID_TYPEFILTER);
    } else if (params.searchQuery && !isString(params.searchQuery)) {
      throw new Error(SearchParams.errorMessages.INVALID_SEARCHQUERY);
    } else if (params.metadataList) {
      if (!isArray(params.metadataList)) {
        throw new Error(SearchParams.errorMessages.INVALID_METADATALIST);
      }

      for (let i = 0; i < params.metadataList.length; i += 1) {
        if (
          !(params.metadataList[i] instanceof MetadataSearchTerm) &&
          !(params.metadataList[i] instanceof AdvancedSearchTerm) &&
          !(params.metadataList[i] instanceof SearchTerm)
        ) {
          throw new Error(SearchParams.errorMessages.INVALID_METADATALIST);
        }
      }
    } else if (params.tagList) {
      if (!isArray(params.tagList)) {
        throw new Error(SearchParams.errorMessages.INVALID_TAGLIST);
      }

      for (let i = 0; i < params.tagList.length; i += 1) {
        if (!(params.tagList[i] instanceof SearchTag)) {
          throw new Error(SearchParams.errorMessages.INVALID_TAGLIST);
        }
      }
    }

    super(params);
  }

  static get errorMessages() {
    return {
      INVALID_FILTERBYLIST: 'filterByList should be an array',
      INVALID_TYPEFILTER: `typeFilter should be a string: ${AllowedTypeFilters.join(', ')}`,
      INVALID_SEARCHQUERY: 'searchQuery should be a string',
      INVALID_METADATALIST: 'metadataList should be an array of MetadataSearchTerm instances',
      INVALID_TAGLIST: 'tagList should be an array of SearchTag instances',
    };
  }

  static get searchTypes() {
    return {
      CATEGORY: 'CATEGORY',
      WORKSPACE: 'WORKSPACE',
      HIGHLIGHT: 'HIGHLIGHT',
      ADVANCED: 'ADVANCED',
      GENERAL: 'GENERAL',
      SAVED: 'SAVED',
    };
  }

  getSearchType() {
    if (this.metadataList && this.metadataList.length > 0) {
      return SearchParams.searchTypes.ADVANCED;
    }

    if (this.tagList && this.tagList.length > 0) {
      // if more than 1 tag, it's a complex search
      if (this.tagList.length > 1) {
        return SearchParams.searchTypes.GENERAL;
      } else if (this.tagList[0] instanceof CategoryTag) {
        return SearchParams.searchTypes.CATEGORY;
      } else if (this.tagList[0] instanceof WorkspaceTag) {
        return SearchParams.searchTypes.WORKSPACE;
      } else if (this.tagList[0] instanceof HighlightSetTag) {
        return SearchParams.searchTypes.HIGHLIGHT;
      }
    }

    if (this.searchQuery && this.searchQuery !== '') {
      return SearchParams.searchTypes.GENERAL;
    }

    if (this.savedSearch && this.savedSearch !== '') {
      return SearchParams.searchTypes.SAVED;
    }

    return SearchParams.searchTypes.GENERAL;
  }

  // check if user has run a search query
  // should return false if a category/workspace/set is selected.
  isSearch() {
    const searchTypes = [SearchParams.searchTypes.GENERAL,
      SearchParams.searchTypes.ADVANCED, SearchParams.searchTypes.SAVED];
    return searchTypes.includes(this.getSearchType());
  }

  getCategoryDataByID(categoryList) {
    if (!this.isSearch()) return getCatDataByID(categoryList, this.tagList[0].value);
    return undefined;
  }

  toSearchQuery() {
    const fullSearchQuery = [];

    if (this.searchQuery) {
      fullSearchQuery.push(`[[${this.searchQuery}]]`);
    }

    if (this.savedSearch) {
      fullSearchQuery.push(this.savedSearch);
    }

    if (this.metadataList && this.metadataList.length > 0) {
      const searchQuery = compact(this.metadataList.map(
        // the conditional removes the operator from the first search term so you won't end up
        // with something like "AND META:'CAT.NAME,value'"
        (item, i) => i > 0 ? item.toSearchQueryWithOperator() : item.toSearchQuery()
      ));
      if (searchQuery.length > 0) {
        fullSearchQuery.push(`(${searchQuery.join(' ')})`);
      }
    }

    if (this.tagList && this.tagList.length > 0) {
      fullSearchQuery.push(
        compact(this.tagList.map(item => item.toSearchQuery())).join(' AND ')
      );
    }

    if (this.filterByList && this.filterByList.length > 0) {
      fullSearchQuery.push(
        compact(this.filterByList.map(item => item.toSearchQuery())).join(' AND ')
      );
    }

    if (fullSearchQuery.length < 1) {
      // since there are no search params, the filters below shouldn't be applied,
      // so just return empty
      return '';
    }

    if (this.typeFilter && this.typeFilter !== '' && this.typeFilter !== 'ANY') {
      fullSearchQuery.push(new SearchTerm('IMAGE_TYPE', this.typeFilter).toSearchQuery());
    }

    return compact(fullSearchQuery).join(' AND ');
  }

  isEmpty() {
    return (
      !this.filterByList &&
      !this.typeFilter &&
      !this.searchQuery &&
      !this.metadataList &&
      !this.tagList &&
      !this.savedSearch);
  }
}

export class SearchTag extends new Record({
  tagType: undefined,
  searchTerm: undefined,
  label: undefined,
  description: undefined,
}) {
  constructor(tagType, searchKeyword, value, label, description) {
    if (!tagType || !value) {
      throw new Error(SearchTag.errorMessages.INVALID_ARGS);
    } else if (SearchTag.validTagTypes.indexOf(tagType) === -1) {
      throw new Error(SearchTag.errorMessages.INVALID_TAGTYPE);
    }

    super({
      tagType,
      searchTerm: new SearchTerm(searchKeyword, value),
      label,
      description,
    });
  }

  static get errorMessages() {
    return {
      INVALID_ARGS: 'Invalid arguments given; requires tagType, searchKeyword and value',
      INVALID_TAGTYPE: `tagType should be either ${SearchTag.validTagTypes.join(', ')}`,
    };
  }

  static get validTagTypes() {
    return ['category', 'workspace', 'highlight', 'search', 'freeText', 'alternates'];
  }

  get keyword() {
    return this.searchTerm.keyword;
  }

  get value() {
    return this.searchTerm.value;
  }

  toSearchQuery() {
    return this.searchTerm.toSearchQuery();
  }
}

export class WorkspaceTag extends SearchTag {
  constructor(value, label, description) {
    super('workspace', 'IN_CATEGORY', value, label, description);
  }
}

export class CategoryTag extends SearchTag {
  constructor(value, label, description) {
    super('category', 'IN_CATEGORY', value, label, description);
  }
}

export class HighlightSetTag extends SearchTag {
  constructor(value, label, description) {
    super('highlight', 'IN_CATEGORY', value, label, description);
  }
}

export class FreeTextTag extends SearchTag {
  constructor(value, label, description) {
    super('freeText', appConfig.baseSearchWord || 'WORDS', value, label, description);
  }

  toSearchQuery() {
    /**
    const splitIndexes = [];
    for (let i = 0; i < this.value.length; i += 1) {
      if (this.value[i] === ' ') {
        splitIndexes.push(i);
      }
    }

    let currentSplitIndex = 0;
    let currentOpeningQuoteIndex = null;
    const initialSplit = [];
    for (let i = 0; i < this.value.length; i += 1) {
      if (this.value[i] === ' ' && currentOpeningQuoteIndex === null) {
        initialSplit.push(trim(this.value.substring(currentSplitIndex, i), '"'));
        currentSplitIndex = i + 1;
      } else if (this.value[i] === '"' && currentOpeningQuoteIndex === null) { // found an opening quote
        currentOpeningQuoteIndex = i;
      } else if (this.value[i] === '"') { // found a closing quote
        currentOpeningQuoteIndex = null;
      }

      if (i === this.value.length - 1) {
        initialSplit.push(trim(this.value.substring(currentSplitIndex), '"'));
      }
    }

    const querySplit = filter(initialSplit, value => isString(value) && value.length > 0);

    const searchQueryArray = querySplit.map((value) => {
      if (value.toUpperCase() === 'OR') {
        return 'OR';
      } else if (value.toUpperCase() === 'AND') {
        return 'AND';
      } else if (value.includes(':')) {
        const splitValue = value.split(':');
        if (splitValue.length > 1) {
          return `${splitValue[0]}:"${splitValue[1].replace(/"/g, '')}"`;
        }
      }
      return `${this.keyword}:"${value}"`;
    });

    const prefix = searchQueryArray.length > 1 ? '(' : '';
    const suffix = searchQueryArray.length > 1 ? ')' : '';

    let searchQueryString = '';

    searchQueryArray.forEach((value, i) => {
      if (value.toUpperCase() === 'OR') {
        searchQueryString += ' OR ';
      } else if (value.toUpperCase() === 'AND') {
        searchQueryString += ' AND ';
      } else if (
        searchQueryArray[i + 1] &&
        (searchQueryArray[i + 1].toUpperCase() === 'OR' ||
        searchQueryArray[i + 1].toUpperCase() === 'AND')
      ) {
        searchQueryString += value;
      } else {
        searchQueryString += `${value}${i === searchQueryArray.length - 1 ? '' : ' AND '}`;
      }
    });

    return !this.value ? '' : `${prefix}${searchQueryString}${suffix}`;
    //return !this.value ? '' : `${prefix}${searchQueryArray.join(' AND ')}${suffix}`;
    **/

    let value = '';
    if (this.value) value = this.value.replace(/"/g, '\\"');

    return !this.value ? '' : `${this.keyword}:"${value}"`;
  }
}

export class DynamicTag extends SearchTag {
  constructor(value, label, description) {
    super('search', null, value, label, description);
  }
}

export class AlternatesTag extends SearchTag {
  constructor(value, label, description) {
    super('alternates', 'REPRESENTATIVES', value, label, description);
  }

  toSearchQuery() {
    return `${this.keyword}:${this.value}`;
  }
}



// WEBPACK FOOTER //
// ./src/js/utils/searchUtils.js